import type { ItemType } from 'antd/es/menu/interface';
import { TFunction } from 'i18next';
import { useCallback, useMemo } from 'react';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { useAdditionalFormContext } from '../../common/form';
import { useAppendFormTableRow } from '../../common/form/table/tanstack/useAppendFormTableRow';
import { useFocusTableRowField } from '../../common/form/table/tanstack/useFocusTableRowField';
import {
  DataObjectDimensionType,
  DataObjectOutputType,
  DataObjectType,
  DimensionValidFormDataType,
} from '../types';
import translations from './CreateDimensionRowButton.translations.json';
import { ButtonProps, Dropdown } from 'antd';
import { useSystemClassifiers } from '../../configuration/useSystemClassifiers';
import { CreateTableRowButton } from '../../common/form/table/tanstack/CreateTableRowButton';
import { DataObjectDescriptionVersionStore } from '../DataObjectDescriptionVersionStore';
import { useWatch } from 'react-hook-form';
import { keepStaleInSeconds } from '../../common/queryClient/utils';
import { getMapBy } from '../../common/utils';
import { useClassifierGroupsQuery } from '../../classifier/query/useClassifierGroupsQuery';
import { ClassifierGroupType } from '../../classifier';
import { DropdownItem } from '../../common/components';

const { addTranslations, useTranslation } = i18nNsFactory(
  'create-dimension-row-button',
);
addTranslations(translations);

export interface CreateDimensionRowButtonProps extends Omit<ButtonProps, 'id'> {
  subjectAttributeDimensions: { code: string; name: string }[];
}

function createDefaultDimension(
  defaultWidth: number,
): DimensionValidFormDataType {
  return {
    id: null,
    classifier: null,
    description: null,
    width: defaultWidth,
    sequenceNumber: null,
    constantValue: null,
    code: '',
    name: '',
    aggregateHierarchy: null,
    dimensionType: null,
    required: null,
  };
}

function getMenuOptions(
  dataSubjectAttributes: { code: string; name: string }[],
  createDimension: (
    dimensionType: string,
    code?: { code: string; name: string },
  ) => void,
  outputTypeCode: DataObjectOutputType | null,
  t: TFunction,
) {
  const options: ItemType[] = [];

  if (dataSubjectAttributes.length) {
    options.push({
      key: 'subject.group',
      label: t('menu.subject'),
      children: dataSubjectAttributes.map((attr) => {
        return {
          key: attr.code,
          onClick: () =>
            createDimension(DataObjectDimensionType.SUBJECT_ATTRIBUTE, attr),
          label: attr.name,
        };
      }),
    });
  }

  options.push({
    key: 'classifier',
    onClick: () => createDimension(DataObjectDimensionType.CLASSIFIER),
    label: <DropdownItem>{t('menu.classifier')}</DropdownItem>,
  });

  options.push({
    key: 'constant',
    onClick: () => createDimension(DataObjectDimensionType.CONSTANT),
    label: <DropdownItem>{t('menu.constant')}</DropdownItem>,
  });

  if (outputTypeCode === DataObjectOutputType.REPORT) {
    options.push({
      key: 'keyBased',
      onClick: () => createDimension(DataObjectDimensionType.KEY_BASED),
      label: <DropdownItem>{t('menu.keyBased')}</DropdownItem>,
    });
  }

  return options;
}

export const CreateDimensionRowButton = (
  props: CreateDimensionRowButtonProps,
) => {
  const { t } = useTranslation();
  const {
    DIMENSION_TYPE: { systemClassifierItems: dimensionTypes },
    DEFAULT_INDICATOR_DIMENSION_WIDTH: {
      systemClassifierItems: [defaultWidth],
    },
  } = useSystemClassifiers(
    'DIMENSION_TYPE',
    'DEFAULT_INDICATOR_DIMENSION_WIDTH',
  );

  const { data: classifierGroups } = useClassifierGroupsQuery(
    keepStaleInSeconds(5),
  );
  const classifierGroupsByCode = useMemo(
    () => getMapBy(classifierGroups || [], 'code'),
    [classifierGroups],
  );

  const [dataObjectDescriptionVersion] =
    DataObjectDescriptionVersionStore.useValue('dataObjectDescriptionVersion');

  const { onChange: formOnChange } = useAdditionalFormContext();

  const outputTypeCode = useWatch({ name: 'outputTypeCode' });
  const focusField = useFocusTableRowField('classifier');
  const appendRow = useAppendFormTableRow();
  const handleAppend = useCallback(
    (dimensionType: string, attr?: { code: string; name: string }) => {
      const row = createDefaultDimension(Number(defaultWidth.code));
      row.dimensionType =
        dimensionTypes.find((item) => item.code === dimensionType) ?? null;
      if (attr) {
        row.code = attr.code;
        row.name = attr.name;
      }

      if (
        dimensionType === DataObjectDimensionType.CONSTANT ||
        dimensionType === DataObjectDimensionType.KEY_BASED ||
        (dataObjectDescriptionVersion.dataObjectType === DataObjectType.IN &&
          dimensionType === DataObjectDimensionType.CLASSIFIER)
      ) {
        row.required = true;
      }

      if (dimensionType === DataObjectDimensionType.SUBJECT_ATTRIBUTE) {
        const groupClassifier: ClassifierGroupType | undefined = attr?.code
          ? classifierGroupsByCode.get(attr.code)
          : undefined;
        if ((groupClassifier?.categoryHierarchyLevelNumbers || []).length > 1) {
          row.aggregateHierarchy = false;
        }
      }
      appendRow(row);
      focusField();
      formOnChange?.();
    },
    [
      appendRow,
      dataObjectDescriptionVersion.dataObjectType,
      defaultWidth.code,
      dimensionTypes,
      focusField,
      formOnChange,
      classifierGroupsByCode,
    ],
  );

  if (dataObjectDescriptionVersion.dataObjectType === DataObjectType.IN) {
    return (
      <CreateTableRowButton
        id="create-dimension"
        title={t('button.add-dimension')}
        onClick={() => handleAppend(DataObjectDimensionType.CLASSIFIER)}
      />
    );
  }

  return (
    <Dropdown
      menu={{
        items: getMenuOptions(
          props.subjectAttributeDimensions,
          handleAppend,
          outputTypeCode,
          t,
        ),
      }}
    >
      <CreateTableRowButton
        id="create-dimension"
        title={t('button.add-dimension')}
      />
    </Dropdown>
  );
};
